/* eslint-disable unicorn/prefer-module */
import { useCallback, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import {
  ESCENARIOS_CRECIMIENTO_INVERSIONES,
  FACTOR_CAPTACION_NO_INVERSORES,
  IPSOS_INVERSIONES,
  TICKETS_INVERSION,
} from "../../data/ipsos2025/inversiones";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaInversiones.module.scss";

export const Inversiones = () => {
  const { formatNumber, formatUSD, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [canalSeleccionado, setCanalSeleccionado] = useState("Banca privada");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(10);
  const [ticketPromedio, setTicketPromedio] = useState(TICKETS_INVERSION[1].valor);

  // Calcular mercado con IPSOS
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Inversores y no inversores
    const inversores = Math.round(totalEjecutivos * IPSOS_INVERSIONES.realizaInversiones.valor);
    const noInversores = Math.round(totalEjecutivos * IPSOS_INVERSIONES.noRealizaInversiones.valor);

    // Desglose por tipo de inversión (sobre los inversores)
    const tiposInversion = {};
    Object.entries(IPSOS_INVERSIONES.tiposInversion).forEach(([tipo, dato]) => {
      tiposInversion[tipo] = {
        dato,
        personas: Math.round(inversores * dato.valor),
        pct: dato.valor * 100,
      };
    });

    // Desglose por prioridad (sobre los inversores)
    const prioridades = {};
    Object.entries(IPSOS_INVERSIONES.prioridades).forEach(([prioridad, dato]) => {
      prioridades[prioridad] = {
        dato,
        personas: Math.round(inversores * dato.valor),
        pct: dato.valor * 100,
      };
    });

    // Desglose por canal (sobre los inversores)
    const canales = {};
    Object.entries(IPSOS_INVERSIONES.canales).forEach(([canal, dato]) => {
      canales[canal] = {
        dato,
        personas: Math.round(inversores * dato.valor),
        pct: dato.valor * 100,
      };
    });

    // Mercado potencial (inversores * ticket promedio)
    const mercadoActual = inversores * ticketPromedio;
    const mercadoPotencialNoInversores = noInversores * ticketPromedio;

    return {
      totalEjecutivos,
      inversores,
      noInversores,
      tiposInversion,
      prioridades,
      canales,
      mercadoActual,
      mercadoPotencialNoInversores,
      mercadoTotal: mercadoActual + mercadoPotencialNoInversores,
    };
  }, [totalEjecutivos, ticketPromedio]);

  // Calcular oportunidad por canal
  const calcularOportunidadCanal = useCallback(
    (canal, porcentajeCrecimiento) => {
      if (!resultados) return { clientesGanados: 0, mercadoGanado: 0 };

      const marketShareActual = IPSOS_INVERSIONES.canales[canal]?.valor || 0;
      const nuevoMarketShare = marketShareActual * (1 + porcentajeCrecimiento / 100);

      const clientesActuales = Math.round(resultados.inversores * marketShareActual);
      const clientesNuevos = Math.round(resultados.inversores * nuevoMarketShare);
      const clientesGanados = clientesNuevos - clientesActuales;

      // Además, captar del 74% que no invierte (supuesto: fracción del crecimiento aplica a nuevos inversores)
      const noInversoresCaptados = Math.round(
        resultados.noInversores * (porcentajeCrecimiento / 100) * FACTOR_CAPTACION_NO_INVERSORES.valor
      );

      const totalClientesGanados = clientesGanados + noInversoresCaptados;
      const mercadoGanado = totalClientesGanados * ticketPromedio;

      return {
        clientesActuales,
        clientesNuevos,
        clientesGanados,
        noInversoresCaptados,
        totalClientesGanados,
        marketShareActual: marketShareActual * 100,
        marketShareNuevo: nuevoMarketShare * 100,
        mercadoGanado,
      };
    },
    [resultados, ticketPromedio]
  );

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  return (
    <div className={styles.inversionesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>Mercado Inversiones</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.inversionesContent}>
        {/* Tabla resumen */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thSmall)}>Sí invierten</th>
                  <th className={cx("text-center", styles.thSmall)}>No invierten</th>
                  <th className={cx("text-center", styles.thTicket)}>Ticket prom. (supuesto)</th>
                  <th className={cx("text-center", styles.thMercado)}>Mercado potencial</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={cx("text-center align-middle", styles.celdaSi)}>
                    <div className={styles.celdaContenido}>
                      <BadgeDato dato={IPSOS_INVERSIONES.realizaInversiones}>
                        {(IPSOS_INVERSIONES.realizaInversiones.valor * 100).toFixed(0)}%
                      </BadgeDato>
                      <div className={styles.valorGrande}>{formatNumber(resultados.inversores)}</div>
                      <div className={styles.subtexto}>inversores</div>
                    </div>
                  </td>
                  <td className={cx("text-center align-middle", styles.celdaOportunidad)}>
                    <div className={styles.celdaContenido}>
                      <BadgeDato dato={IPSOS_INVERSIONES.noRealizaInversiones} className={styles.badgeOportunidad}>
                        {(IPSOS_INVERSIONES.noRealizaInversiones.valor * 100).toFixed(0)}%
                      </BadgeDato>
                      <div className={styles.valorGrande}>{formatNumber(resultados.noInversores)}</div>
                      <div className={styles.subtexto}>oportunidad de captación</div>
                    </div>
                  </td>
                  <td className="text-center align-middle">
                    <div className={styles.celdaContenido}>
                      <select
                        value={ticketPromedio}
                        onChange={(e) => setTicketPromedio(Number(e.target.value))}
                        className="form-select form-select-sm"
                        style={{ width: "130px" }}
                      >
                        {TICKETS_INVERSION.map((t) => (
                          <option key={t.valor} value={t.valor}>
                            {formatUSD(t.valor)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className={cx("text-center align-middle", styles.celdaTotal)}>
                    <div className={styles.valorDestacado}>{formatUSD(resultados.mercadoTotal)}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Tablas de desglose: Tipos de inversión + Prioridades */}
        {resultados && (
          <div className={styles.tablasDesglose}>
            <div className={styles.desgloseContainer}>
              <h4>¿En qué invierten?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Tipo de inversión</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.tiposInversion).map(([tipo, data]) => (
                    <tr key={tipo}>
                      <td className="text-start">{tipo}</td>
                      <td className="text-center">
                        <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.desgloseContainer}>
              <h4>¿Qué priorizan al invertir?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Prioridad</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.prioridades).map(([prioridad, data]) => (
                    <tr key={prioridad}>
                      <td className="text-start">{prioridad}</td>
                      <td className="text-center">
                        <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Market Share por Canal */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share por Canal de Inversión</h4>
            <div className={styles.marketShareGrid}>
              {Object.entries(resultados.canales).map(([canal, data]) => (
                <div key={canal} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{canal}</div>
                  <div className={styles.cardShare}>
                    <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Personas</span>
                      <span className={styles.metricaValor}>{formatNumber(data.personas)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Ipsos 2025 (lámina 77, base 400/106):</strong> el 44,9% prioriza rentabilidad, 33,4% seguridad,
            24,6% usa plataformas online, 17,6% invierte en criptomonedas.
          </p>
          <p>
            <strong>Supuestos comerciales:</strong> ticket promedio de inversión (USD) y fracción de no inversores
            captados por punto de crecimiento simulado.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad por Canal</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>Canal objetivo:</label>
                    <select
                      value={canalSeleccionado}
                      onChange={(e) => setCanalSeleccionado(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {Object.keys(IPSOS_INVERSIONES.canales).map((canal) => (
                        <option key={canal} value={canal}>
                          {canal}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% crecimiento (supuesto):</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CRECIMIENTO_INVERSIONES.map((p) => (
                        <option key={p.valor} value={p.valor}>
                          +{p.valor}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidadCanal(canalSeleccionado, porcentajeCaptacion);
                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Clientes actuales ({canalSeleccionado}):</span>
                        <span className={styles.resultadoValor}>{formatNumber(oportunidad.clientesActuales)}</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Clientes ganados (del share):</span>
                        <span className={styles.resultadoValorDestacado}>
                          +{formatNumber(oportunidad.clientesGanados)}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Nuevos inversores captados (del 74%):</span>
                        <span className={styles.resultadoValorDestacado}>
                          +{formatNumber(oportunidad.noInversoresCaptados)}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado ganado:</span>
                        <span className={styles.resultadoValorDestacado}>{formatUSD(oportunidad.mercadoGanado)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inversiones;
